import React, { useState } from 'react';
import { API_BASE_URL } from '../config/api';
import { Modal, Form, Row, Col, Button, Alert } from 'react-bootstrap';

const INITIAL_FORM = {
  type: 'maintenance',
  description: '',
  amount: '',
  cost_date: new Date().toISOString().split('T')[0],
  notes: '',
  vehicle_id: '',
};

/**
 * Shared modal for adding financial entries (car costs).
 *
 * Props:
 *  - show / onHide          – modal visibility
 *  - onSuccess(type)        – called after a successful save
 *  - apiCall                – authenticated fetch wrapper
 *  - userRole               – current user's role
 *  - vehicleId (optional)   – pre-selected vehicle (VehicleDetail)
 *  - vehicles  (optional)   – array for vehicle selector dropdown (VehicleList)
 *  - vehicleStatus (optional) – controls "Sold" option visibility
 */
export function AddFinancialEntryModal({
  show,
  onHide,
  onSuccess,
  apiCall,
  userRole,
  vehicleId,
  vehicles,
  vehicleStatus,
}) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState(null);

  const canAddPurchaseCost = userRole === 'manager' || userRole === 'admin';
  const canAddAdjustment = userRole === 'admin';
  const canAddEntry = userRole === 'manager' || userRole === 'admin';

  // Derive selected vehicle's status: from prop (VehicleDetail) or from dropdown selection (VehicleList)
  const selectedStatus = vehicleId
    ? vehicleStatus
    : vehicles?.find((v) => String(v.id) === String(form.vehicle_id))?.status;
  const hasVehicle = !!(vehicleId || form.vehicle_id);
  const showSoldOption = hasVehicle && selectedStatus !== 'sold';

  const resetAndClose = () => {
    setForm(INITIAL_FORM);
    setError(null);
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const targetVehicleId = vehicleId || form.vehicle_id;
    if (!targetVehicleId) {
      setError('Please select a vehicle');
      return;
    }

    try {
      const response = await apiCall(
        `${API_BASE_URL}/vehicles/${targetVehicleId}/car-costs`,
        {
          method: 'POST',
          body: JSON.stringify({
            type: form.type,
            description: form.description,
            amount: form.amount,
            cost_date: form.cost_date,
            notes: form.notes,
          }),
        }
      );

      if (response.error) {
        throw new Error(response.error);
      }

      const savedType = form.type;
      setForm(INITIAL_FORM);
      setError(null);
      onHide();
      onSuccess?.(savedType);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Modal show={show} onHide={resetAndClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Financial Entry</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Vehicle selector – only when no vehicleId is provided */}
          {!vehicleId && vehicles && (
            <Form.Group className="mb-3">
              <Form.Label>Select Vehicle *</Form.Label>
              <Form.Select
                value={form.vehicle_id}
                onChange={(e) => {
                  const vid = e.target.value;
                  const vStatus = vehicles?.find((v) => String(v.id) === String(vid))?.status;
                  const resetType = form.type === 'sold' && vStatus === 'sold';
                  setForm({ ...form, vehicle_id: vid, ...(resetType && { type: 'maintenance', description: '' }) });
                }}
                required
              >
                <option value="">Select a vehicle...</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.year} {v.make} {v.model} - {v.license_plate}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Entry Type *</Form.Label>
            <Form.Select
              value={form.type}
              onChange={(e) => {
                const newType = e.target.value;
                const autoDesc = { purchase_cost: 'Purchase', sold: 'Sold' };
                setForm({ ...form, type: newType, description: autoDesc[newType] || '' });
              }}
              required
            >
              <option value="maintenance">Maintenance</option>
              <option value="repair">Repair</option>
              {canAddPurchaseCost && <option value="purchase_cost">Purchase Cost</option>}
              {canAddAdjustment && <option value="adjustment">Adjustment</option>}
              {canAddAdjustment && <option value="depreciation">Depreciation</option>}
              {canAddEntry && showSoldOption && <option value="sold">Sold</option>}
            </Form.Select>
            {!canAddAdjustment && (
              <Form.Text className="text-muted">
                Only administrators can add Adjustment or Depreciation entries.
              </Form.Text>
            )}
          </Form.Group>

          {form.type !== 'purchase_cost' && form.type !== 'sold' && (
            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="e.g., Oil change, Brake pad replacement"
                required
              />
            </Form.Group>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{form.type === 'sold' ? 'Sold Price ($) *' : 'Amount ($) *'}</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
                <Form.Text className="text-muted">
                  {form.type === 'sold'
                    ? 'Enter the sale price (recorded as revenue)'
                    : 'Enter positive amount (will be recorded as expense)'}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Date *</Form.Label>
                <Form.Control
                  type="date"
                  value={form.cost_date}
                  onChange={(e) => setForm({ ...form, cost_date: e.target.value })}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Additional notes or details..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetAndClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Save Entry
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default AddFinancialEntryModal;
